import React from 'react'
import { Box, Collapse, IconButton, LinearProgress, Paper, Typography } from '@mui/material'

import {
  UI_COLOR_BORDER_METRIC,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_PROGRESS_TRACK,
  UI_COLOR_SHADOW_CARD,
  UI_COLOR_TEXT_CARD_TITLE,
  UI_COLOR_TEXT_SECTION,
  UI_COLOR_TEXT_SECTION_MUTED,
  UI_COLOR_WHITE
} from '../../../constants/uiColors'
import { UI_TEXT_PENDING, UI_TEXT_PROGRESS, UI_TEXT_SUMMARY } from '../../../constants/uiText'
import GoogleIcon from '../../icon/GoogleIcon'

const toNumber = (value) => {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : 0
}

const formatCount = (value) => {
  const numberValue = toNumber(value)
  return Number.isInteger(numberValue) ? String(numberValue) : numberValue.toFixed(2)
}

function SummaryMetric({
  icon,
  title,
  subtitle,
  current,
  total,
  pending,
  pendingLabel = UI_TEXT_PENDING,
  iconColor = UI_COLOR_PRIMARY_ALT
}) {
  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 134,
        border: `1px solid ${UI_COLOR_BORDER_METRIC}`,
        borderRadius: '6px',
        p: 2,
        backgroundColor: UI_COLOR_WHITE,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <GoogleIcon name={icon} size={18} color={iconColor} />
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: UI_COLOR_TEXT_SECTION }}>
          {title}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: 12, color: UI_COLOR_TEXT_SECTION_MUTED, mb: 0.75 }}>
        {subtitle}
      </Typography>

      <Typography
        sx={{ fontSize: 34, lineHeight: 1.1, fontWeight: 800, color: UI_COLOR_PRIMARY_ALT }}
      >
        {formatCount(current)}/{formatCount(total)}
      </Typography>

      <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI_COLOR_TEXT_SECTION, mt: 1 }}>
        {pendingLabel}: {formatCount(pending)}
      </Typography>
    </Box>
  )
}

/**
 * Cómo reutilizar este componente:
 *
 * <ProgressSummaryCard
 *   title="RESUMEN DE DEVOLUCIÓN"
 *   progressLabel="Progreso de conteo"
 *   progressPercent={75}
 *   metrics={[
 *     {
 *       key: 'products',
 *       icon: 'inventory_2',
 *       title: 'PRODUCTOS',
 *       subtitle: 'Progreso de líneas',
 *       current: 3,
 *       total: 4,
 *       pending: 1
 *     }
 *   ]}
 * />
 *
 * Props:
 * - title: título principal del card.
 * - progressLabel: texto junto a la barra de progreso.
 * - progressPercent: porcentaje numérico entre 0 y 100.
 * - metrics: lista de métricas que se pintan como cajas dentro del card.
 * - metric.key: identificador único opcional; si no viene se usa el title.
 * - metric.icon / title / subtitle: icono y textos visibles de la métrica.
 * - metric.current / total / pending: números que se muestran en la métrica.
 * - metric.pendingLabel / iconColor: personalización opcional de texto y color.
 * - cardSx: estilos extra para el Paper principal.
 * - metricColumns: columnas responsive para la grilla de métricas.
 * - progressColor / progressTrackColor: colores de la barra de progreso.
 * - collapsible: muestra un control para abrir/cerrar el contenido del resumen.
 * - collapseMetricsOnly: al cerrar, mantiene visible la barra de progreso y oculta métricas.
 */
export default function ProgressSummaryCard({
  title = UI_TEXT_SUMMARY,
  progressLabel = UI_TEXT_PROGRESS,
  progressPercent = 0,
  metrics = [],
  cardSx = {},
  metricColumns = { xs: '1fr', md: '1fr 1fr' },
  progressColor = UI_COLOR_PRIMARY_ALT,
  progressTrackColor = UI_COLOR_PROGRESS_TRACK,
  collapsible = false,
  defaultExpanded = true,
  titleSx = {},
  collapsibleHeaderSx = {},
  toggleSx = {},
  toggleIconSize = 28,
  progressContainerSx = {},
  progressLabelSx = {},
  progressPercentSx = {},
  progressBarSx = {},
  contentSx = {},
  collapseOpenLabel = 'Cerrar resumen',
  collapseClosedLabel = 'Abrir resumen',
  collapseMetricsOnly = false
}) {
  const safeProgressPercent = Math.min(Math.max(toNumber(progressPercent), 0), 100)
  const [expanded, setExpanded] = React.useState(defaultExpanded)
  const contentId = React.useId()

  const progressBlock = (
    <Box sx={progressContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.75 }}>
        <Typography
          sx={{ fontSize: 13, fontWeight: 800, color: UI_COLOR_TEXT_SECTION, ...progressLabelSx }}
        >
          {progressLabel}
        </Typography>
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: progressColor, ...progressPercentSx }}
        >
          {Math.round(safeProgressPercent)}%
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={safeProgressPercent}
        sx={{
          height: 8,
          borderRadius: 999,
          backgroundColor: progressTrackColor,
          '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            backgroundColor: progressColor
          },
          ...progressBarSx
        }}
      />
    </Box>
  )

  const metricsBlock = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: metricColumns,
        gap: 2,
        alignItems: 'stretch'
      }}
    >
      {metrics.map(({ key, ...metric }) => (
        <SummaryMetric key={key || metric.title} {...metric} />
      ))}
    </Box>
  )

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: '10px',
        mb: 2,
        backgroundColor: UI_COLOR_WHITE,
        boxShadow: UI_COLOR_SHADOW_CARD,
        ...cardSx
      }}
    >
      {collapsible ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              alignItems: 'center',
              gap: 2,
              mb: expanded || collapseMetricsOnly ? 2 : 0,
              ...collapsibleHeaderSx
            }}
          >
            <Typography
              sx={{
                minWidth: 0,
                fontSize: 16,
                fontWeight: 800,
                color: UI_COLOR_TEXT_CARD_TITLE,
                ...titleSx
              }}
            >
              {title}
            </Typography>
            <IconButton
              aria-label={expanded ? collapseOpenLabel : collapseClosedLabel}
              aria-expanded={expanded}
              aria-controls={contentId}
              onClick={() => setExpanded((current) => !current)}
              sx={{ color: progressColor, ...toggleSx }}
            >
              <GoogleIcon
                name={expanded ? 'expand_less' : 'expand_more'}
                size={toggleIconSize}
                color="inherit"
              />
            </IconButton>
          </Box>

          {collapseMetricsOnly && progressBlock}

          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box id={contentId} sx={contentSx}>
              {collapseMetricsOnly ? (
                metrics.length > 0 && <Box sx={{ mt: 2 }}>{metricsBlock}</Box>
              ) : (
                <>
                  {progressBlock}
                  {metrics.length > 0 && <Box sx={{ mt: 2 }}>{metricsBlock}</Box>}
                </>
              )}
            </Box>
          </Collapse>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              alignItems: 'start',
              gap: 2,
              mb: 1.5
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 800,
                color: UI_COLOR_TEXT_CARD_TITLE,
                ...titleSx
              }}
            >
              {title}
            </Typography>

            {progressBlock}
          </Box>

          {metricsBlock}
        </>
      )}
    </Paper>
  )
}
